// Spec 45 component-behavior — cmp_ds_switch + aria_switch.
//
// Switch is a 2-state toggle (on/off) with role="switch" + aria-checked.
// Functionally equivalent to a checkbox but communicates "settings
// toggle" semantic instead of "form input check". Per WAI-ARIA, a
// switch's checked state is communicated via aria-checked.
//
// Implementation: native <button role="switch"> rather than a hidden
// <input type="checkbox"> + visual-only switch. The button form lets
// us control the click/keyboard semantic without faking native form
// submit; consumers who need form submission should use Checkbox.
//
// Keyboard: Space + Enter both toggle (the spec model says Space-only,
// but Enter also toggling matches user expectation across browsers).

import { forwardRef, type ButtonHTMLAttributes } from "react";

import { cx } from "../lib/cx.ts";

export interface SwitchProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type" | "role"> {
  /** Controlled checked value. */
  readonly checked: boolean;
  /** Fires when the user toggles. Caller MUST flip `checked` for the next render. */
  readonly onCheckedChange: (next: boolean) => void;
  readonly error?: boolean;
}

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  (
    {
      checked,
      onCheckedChange,
      error = false,
      disabled,
      className,
      onClick,
      ...rest
    },
    ref,
  ) => (
    <button
      ref={ref}
      type="button"
      role="switch"
      data-component="switch"
      data-checked={checked || undefined}
      data-error={error || undefined}
      aria-checked={checked}
      aria-invalid={error || undefined}
      disabled={disabled === true}
      className={cx("nai-switch", className)}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) onCheckedChange(!checked);
      }}
      {...rest}
    />
  ),
);

Switch.displayName = "Switch";
